import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const processSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  align: z.enum(['left', 'center']).default('left'),
  steps: z
    .array(
      z.object({
        number: z.string().max(4),
        tag: z.string().max(80).optional(),
        title: z.string().min(1).max(160),
        description: z.string().max(500),
        meta: z.string().max(200).optional(),
      }),
    )
    .min(2)
    .max(10),
});

export type ProcessData = z.infer<typeof processSchema>;

export function ProcessRenderer({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  steps,
}: ProcessData) {
  const safeSteps = Array.isArray(steps) ? steps : [];
  const headCls = `v5-section-head${align === 'left' ? ' v5-section-head-left' : ''}`;
  return (
    <section className="v5-section v5-proceso-section">
      <div className="container">
        <div className={headCls}>
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-pp-h2">{title}</h2>
          {subtitle ? <p className="v5-pp-sub">{subtitle}</p> : null}
        </div>
        <div className="v5-pp-list">
          {safeSteps.map((step, i) => (
            <article key={i} className="v5-pp-phase">
              <div className="v5-pp-phase-num">{step.number}</div>
              <div className="v5-pp-rail" aria-hidden>
                <span className="v5-pp-dot"></span>
              </div>
              <div className="v5-pp-card">
                {step.tag ? <div className="v5-pp-phase-tag">{step.tag}</div> : null}
                <h3 className="v5-pp-phase-title">{step.title}</h3>
                <p className="v5-pp-phase-desc">{step.description}</p>
                {step.meta ? <div className="v5-pp-phase-meta">{step.meta}</div> : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export const processBlock: BlockDefinition<ProcessData> = {
  meta: {
    type: 'process',
    label: 'Proceso / Fases (v5)',
    description: 'Timeline de fases con número grande, tag, título, descripción y entregables.',
    category: 'content',
    icon: 'list-ordered',
  },
  schema: processSchema,
  defaults: {
    eyebrow: '● Connect to Value®',
    title: 'Las 6 fases que sigue cada operación',
    subtitle: 'Mismo proceso para todas las operaciones, adaptado al tamaño y complejidad.',
    align: 'left',
    steps: [
      { number: '01', tag: 'Fase 1 · 2-3 sem', title: 'Análisis y valoración', description: 'Diagnóstico y aplicación de los 3 métodos.', meta: 'Informe de valoración' },
      { number: '02', tag: 'Fase 2 · 2-3 sem', title: 'Estrategia y cuaderno de venta', description: 'Preparación del cuaderno y lista de targets cualificados.', meta: 'Cuaderno de venta' },
      { number: '03', tag: 'Fase 3 · 4-8 sem', title: 'Contacto cualificado', description: 'Activación de la red, contacto discreto, NDAs.', meta: 'NDAs firmados' },
      { number: '04', tag: 'Fase 4 · 4-6 sem', title: 'Visitas y LOIs', description: 'Visitas a la empresa y recepción de ofertas indicativas.', meta: 'LOIs · Selección comprador' },
      { number: '05', tag: 'Fase 5 · 6-10 sem', title: 'DD y negociación SPA', description: 'Data-room, due diligence y negociación del SPA.', meta: 'Data-room · SPA borrador' },
      { number: '06', tag: 'Fase 6 · 2-4 sem', title: 'Firma y cierre', description: 'Notaría, transferencia de fondos y handover ordenado.', meta: 'Cierre · Handover' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'steps',
      label: 'Fases',
      type: 'array',
      max: 10,
      itemFields: [
        { name: 'number', label: 'Número (01)', type: 'text', required: true, max: 4 },
        { name: 'tag', label: 'Tag (Fase X · 2 sem)', type: 'text', max: 80 },
        { name: 'title', label: 'Título', type: 'text', required: true, max: 160 },
        { name: 'description', label: 'Descripción', type: 'textarea', required: true, max: 500 },
        { name: 'meta', label: 'Entregables', type: 'text', max: 200 },
      ],
    },
  ],
  Renderer: ProcessRenderer,
};
